import { Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ShortageChangelogService } from './shortage-changelog.service';
import { AppConstants } from '../../../app.constants';

@Component({
  selector: 'app-shortage-changelog',
  templateUrl: './shortage-changelog.component.html',
  styleUrl: './shortage-changelog.component.scss'
})
export class ShortageChangelogComponent implements OnInit {
  changelogData: any[] = [];
  changelogTypeFilter: string = 'updated';

  // Filter variables
  searchQuery: string = '';
  selectedStatus: string = '';
  dateFrom: string = '';
  dateTo: string = '';

  // Modal variables
  showChangesModal: boolean = false;
  selectedNdc: string = '';
  selectedChanges: any[] = [];

  // NDC Details Modal variables
  showNdcDetailsModal: boolean = false;
  selectedNdcDetails: any = null;
  selectedNdcParent: any = null;

  // Pagination
  currentPage: number = 1;
  pageSize: number = 10;
  totalRecords: number = 0;
  totalPages: number = 0;
  isLoading: boolean = false;

  constructor(private changelogService: ShortageChangelogService) { }

  ngOnInit(): void {
    this.fetchChangelog();
  }

  formatDateToDDMMYYYY(dateString: string): string {
    if (!dateString) return '';
    const parts = dateString.split('-'); // yyyy-mm-dd
    if (parts.length !== 3) return dateString;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }

  fetchChangelog(): void {
    this.isLoading = true;
    const email = localStorage.getItem('email') || "";
    const payload = {
      user_email: email,
      filter_type: this.changelogTypeFilter,
      page: this.currentPage,
      limit: this.pageSize,
      include_history: true,
      filters: {
        search_query: this.searchQuery,
        status: this.selectedStatus,
        date_from: this.formatDateToDDMMYYYY(this.dateFrom),
        date_to: this.formatDateToDDMMYYYY(this.dateTo)
      }
    };

    this.changelogService.getChangelogData(payload).subscribe({
      next: (res) => {
        this.changelogData = this.processChangelogData(res.records || []);
        this.totalRecords = res.total_records || 0;
        this.totalPages = res.total_pages || 0;
        this.isLoading = false;
      },
      error: (err) => {
        console.error("Error fetching changelog", err);
        this.isLoading = false;
      }
    });
  }

  processChangelogData(records: any[]): any[] {
    if (!records || records.length === 0) return [];

    // Check if the response contains ndcs array (grouped format)
    const isGrouped = records.some(r => r.ndcs && Array.isArray(r.ndcs) && r.ndcs.length > 0);

    if (isGrouped) {
      return records.map((record: any) => ({
        ...record,
        isExpanded: false
      }));
    }

    // Otherwise group the flat records by NDA client-side
    const groupedMap = new Map<string, any>();

    records.forEach((record: any) => {
      const ndaKey = record.nda || 'N/A';

      if (!groupedMap.has(ndaKey)) {
        groupedMap.set(ndaKey, {
          nda: record.nda,
          brand_name: record.brand_name,
          isExpanded: false,
          ndcs: []
        });
      }

      const group = groupedMap.get(ndaKey);

      group.ndcs.push({
        ndc: record.ndc,
        strength: record.strength,
        route: record.route,
        dosage_forms: record.dosage_forms,
        latest_record: {
          status: record.status,
          availability_information: record.availability_information,
          type_of_update: record.type_of_update,
          created_at: record.latest_update_time || record.created_at,
          company_name: record.company_name || record.excel_record?.company_name,
          generic_name: record.generic_name,
          date_of_update: record.date_of_update,
          date_discontinued: record.date_discontinued || record.date_discontinuation,
          initial_posting_date: record.initial_posting_date,
          presentation: record.presentation,
          reason_for_shortage: record.reason_for_shortage || record.reason_of_shortage,
          related_information: record.related_information,
          resolved_note: record.resolved_note,
          therapeutic_category: record.therapeutic_category,
          ndc: record.ndc
        },
        previous_record: {
          company_name: record.excel_record?.company_name
        },
        changes: record.changes || []
      });
    });

    return Array.from(groupedMap.values());
  }

  getCompanyNames(item: any): string {
    if (!item || !item.ndcs) return 'N/A';
    const companies = item.ndcs
      .map((ndc: any) => ndc.previous_record?.company_name || ndc.latest_record?.company_name)
      .filter((c: any) => !!c);
    const uniqueCompanies = Array.from(new Set(companies));
    return uniqueCompanies.length > 0 ? uniqueCompanies.join(', ') : 'N/A';
  }

  hasMultipleCompanies(item: any): boolean {
    if (!item || !item.ndcs || item.ndcs.length <= 1) return false;
    const firstCompany = item.ndcs[0]?.previous_record?.company_name || item.ndcs[0]?.latest_record?.company_name || 'N/A';
    return item.ndcs.some((ndc: any) => {
      const comp = ndc.previous_record?.company_name || ndc.latest_record?.company_name || 'N/A';
      return comp !== firstCompany;
    });
  }

  toggleRow(item: any): void {
    item.isExpanded = !item.isExpanded;
  }

  openChangesModal(ndc: any, event: Event): void {
    event.stopPropagation(); // Avoid triggering row toggle if clicked
    this.selectedNdc = ndc.ndc || 'N/A';
    this.selectedChanges = ndc.changes || [];
    this.showChangesModal = true;
  }

  closeChangesModal(): void {
    this.showChangesModal = false;
    this.selectedNdc = '';
    this.selectedChanges = [];
  }

  openNdcDetailsModal(ndc: any, parentItem: any, event: Event): void {
    event.stopPropagation();
    console.log('openNdcDetailsModal ndc:', ndc);
    console.log('openNdcDetailsModal parentItem:', parentItem);
    this.selectedNdcDetails = ndc;
    this.selectedNdcParent = parentItem;
    this.showNdcDetailsModal = true;
  }

  closeNdcDetailsModal(): void {
    this.showNdcDetailsModal = false;
    this.selectedNdcDetails = null;
    this.selectedNdcParent = null;
  }

  getVal(fieldObj: any): any {
    if (!fieldObj) return 'N/A';
    if (typeof fieldObj === 'object' && 'value' in fieldObj) {
      return fieldObj.value !== null && fieldObj.value !== undefined ? fieldObj.value : 'N/A';
    }
    return fieldObj;
  }

  getFormattedDate(dateVal: any): string {
    const val = this.getVal(dateVal);
    if (!val || val === 'N/A') return 'N/A';

    // Try parsing the date
    const d = new Date(val);
    if (isNaN(d.getTime())) {
      return val; // Return original string if not a valid date
    }

    // Format to dd-MM-yyyy
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  }

  formatArrayOrString(val: any): string {
    if (!val) return 'N/A';
    if (Array.isArray(val)) {
      return val.join(', ');
    }
    return val;
  }

  formatFieldName(field: string): string {
    if (!field) return '';
    return field
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  handleFilterChange(): void {
    this.currentPage = 1;
    this.fetchChangelog();
  }

  resetFilters(): void {
    this.searchQuery = '';
    this.selectedStatus = '';
    this.dateFrom = '';
    this.dateTo = '';
    this.changelogTypeFilter = 'updated';
    this.currentPage = 1;
    this.fetchChangelog();
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.fetchChangelog();
    }
  }

  handlePageSizeChange(): void {
    this.currentPage = 1;
    this.fetchChangelog();
  }

  getVisiblePages(): number[] {
    const pages: number[] = [];
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage - 2);
    let end = Math.min(this.totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }
}
